import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProduct, getPublicUserProfile, getMarketplaceLocale } from '../services/api.shop';
import { useFrontendAuth } from '../hooks/useAuth';
import { Product, ProductCondition, ProductStatus, PublicUserProfileDto, ShopMarketplaceLocale } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import SellerRatingStars from '../components/SellerRatingStars';
import { ProductImageFallback } from '../components/ProductCard';
import { Eye, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { formatMoney } from '../utils/productValidation';
import { translateProductCondition } from '../utils/productConditionLabel';
import { translateCategoryName } from '../utils/categoryLabel';
import { formatSpecDisplayValue } from '../utils/booleanDisplay';
import { usePageMeta, CANONICAL_ORIGIN } from '../hooks/usePageMeta';
import {
    createHoldCountdownAnchor,
    formatCountdown,
    isReservationActive,
    remainingFromAnchor,
} from '../utils/reservationCountdown';
import { formatPublicHandle, publicProfileHeading } from '../utils/usernameRules';

/** Soft chip for condition / category - scannable without heavy chrome. */
function MetaChip({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-flex max-w-full items-center truncate rounded-full border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] px-2.5 py-1 text-xs font-medium text-[var(--text-color)]">
            {children}
        </span>
    );
}

const ProductDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageFailed, setImageFailed] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const { user } = useFrontendAuth();

    const productId = id ? parseInt(id, 10) : NaN;

    useEffect(() => {
        setImageFailed(false);
        setCurrentImageIndex(0);
    }, [productId]);

    const viewerKey = user?.id ?? 'anon';
    const { data: product, isLoading: loading, error, refetch } = useQuery<Product>({
        queryKey: ['product', productId, viewerKey],
        queryFn: () => getProduct(productId),
        enabled: !isNaN(productId),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
    });

    const sellerId = product?.sellerId;
    const { data: sellerProfile } = useQuery<PublicUserProfileDto>({
        queryKey: ['publicProfile', sellerId],
        queryFn: () => getPublicUserProfile(sellerId!),
        enabled: !!sellerId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: marketplaceLocale } = useQuery<ShopMarketplaceLocale>({
        queryKey: ['marketplaceLocale'],
        queryFn: getMarketplaceLocale,
        staleTime: 10 * 60 * 1000,
    });

    const productSeo = useMemo(() => {
        if (!product) return null;
        const priceLabel = formatMoney(product.price);
        const title = `${product.title} · €${priceLabel} | Petito`;
        const descRaw = (product.description || product.title || '').replace(/\s+/g, ' ').trim();
        const description = descRaw.slice(0, 160);
        const image = product.imageUrls?.find((u) => !!u?.trim()) ?? null;
        const path = `/products/${product.id}`;
        const available =
            product.status === ProductStatus.Active && !product.isSold
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock';
        const jsonLd: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.title,
            description: descRaw.slice(0, 500),
            image: image ? [image] : undefined,
            url: `${CANONICAL_ORIGIN}${path}`,
            offers: {
                '@type': 'Offer',
                url: `${CANONICAL_ORIGIN}${path}`,
                priceCurrency: 'EUR',
                price: product.price,
                availability: available,
                itemCondition:
                    product.condition === ProductCondition.New
                        ? 'https://schema.org/NewCondition'
                        : 'https://schema.org/UsedCondition',
            },
        };
        return { title, description, path, imageUrl: image, jsonLd };
    }, [product]);

    usePageMeta({
        title: productSeo?.title,
        description: productSeo?.description,
        path: productSeo?.path,
        imageUrl: productSeo?.imageUrl,
        noIndex: product
            ? product.status !== ProductStatus.Active || product.isSold
            : false,
        jsonLd: productSeo?.jsonLd,
    });

    // Prefer product band amount (Vinted-style sizes); fall back to economics default
    const shippingAmount =
        product?.shippingBuyerAmount != null && product.shippingBuyerAmount > 0
            ? product.shippingBuyerAmount
            : marketplaceLocale?.shippingFeeEnabled &&
                marketplaceLocale.domesticShippingAmount != null &&
                marketplaceLocale.domesticShippingAmount > 0
              ? marketplaceLocale.domesticShippingAmount
              : null;

    const shippingHint =
        shippingAmount != null
            ? tr(
                  'checkout.shipping.fromAmount',
                  'From ~€{amount} shipping at checkout; buyer protection calculated at secure checkout.'
              ).replace('{amount}', formatMoney(shippingAmount))
            : tr(
                  'checkout.shipping.hint',
                  'Shipping and buyer protection are calculated at secure checkout.'
              );

    const holdAnchor = useMemo(
        () =>
            createHoldCountdownAnchor(
                product?.holdRemainingSeconds,
                product?.reservedUntil
            ),
        [product?.holdRemainingSeconds, product?.reservedUntil]
    );
    const reservationLive = isReservationActive(
        product?.isReserved,
        product?.reservedUntil,
        nowMs,
        product?.holdRemainingSeconds
    );
    const reservedByMe = !!product?.reservedByMe && reservationLive;
    const holdBlocksBuy = reservationLive && !reservedByMe;
    const countdownLabel = formatCountdown(remainingFromAnchor(holdAnchor, nowMs));

    useEffect(() => {
        if (!reservationLive && !product?.isReserved) return;
        const id = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [
        reservationLive,
        product?.isReserved,
        product?.reservedUntil,
        product?.holdRemainingSeconds,
    ]);

    useEffect(() => {
        if (product?.isReserved && !reservationLive) {
            void refetch();
        }
    }, [product?.isReserved, reservationLive, refetch]);

    const handleBuyNow = () => {
        if (!product || !user) return;
        if (product.isSold || product.status === ProductStatus.Sold) return;
        if (holdBlocksBuy) return;
        navigate(`/checkout/${product.id}`);
    };

    if (loading) return <LoadingSpinner />;
    if (error || !product) {
        const errorMessage =
            error instanceof Error ? error.message : error ? String(error) : 'Product not found';
        return <p className="py-12 text-center text-red-500">{errorMessage}</p>;
    }

    const isOwnProduct = !!user && user.id === product.sellerId;
    const isSold = !!product.isSold || product.status === ProductStatus.Sold;
    const images = (product.imageUrls ?? []).filter((u) => !!u && u.trim().length > 0);
    const mainImage = !imageFailed && images[currentImageIndex] ? images[currentImageIndex] : null;
    const specEntries = Object.entries(product.specifications || {});
    const hasSpecs = specEntries.some(([, specs]) => Object.keys(specs || {}).length > 0);

    const sellerHeading = publicProfileHeading({
        userName: sellerProfile?.userName,
        displayName: sellerProfile?.displayName,
        showPersonLine: sellerProfile?.showPersonLine,
    });
    const sellerName =
        sellerHeading.title ||
        formatPublicHandle(product.sellerFirstName, tr('product.details.seller', 'Seller'));
    const city = (product.sellerCity || sellerProfile?.city || '').trim();
    const region = (product.sellerRegion || sellerProfile?.region || '').trim();
    const place =
        city && region && city.toLowerCase() === region.toLowerCase()
            ? city
            : [city, region].filter(Boolean).join(', ');
    const soldCount = sellerProfile?.itemsSoldCount ?? 0;
    const hasReviews =
        (product.averageSellerRating ?? 0) > 0 && (product.sellerRatingCount ?? 0) > 0;
    const isNewSeller = !hasReviews && soldCount === 0;
    const initials = (sellerName.replace(/^@/, '').charAt(0) || '?').toUpperCase();
    const avatarUrl = sellerProfile?.profilePictureUrl?.trim() || '';
    const memberSince = sellerProfile?.memberSince
        ? new Date(sellerProfile.memberSince).toLocaleDateString(undefined, {
              month: 'short',
              year: 'numeric',
          })
        : null;

    const goToPrev = () => {
        setImageFailed(false);
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };
    const goToNext = () => {
        setImageFailed(false);
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const buyActions = (
        <>
            {isSold ? (
                <div className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-5 text-center">
                    <p className="text-base font-semibold text-[var(--text-color)] sm:text-lg">
                        {tr('product.details.sold.notice', 'This item has been sold')}
                    </p>
                    {isOwnProduct && (
                        <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                            {tr('product.details.sold.own', 'Kept for your listing and order history.')}{' '}
                            <Link to="/my-account/listings" className="font-medium underline">
                                {tr('account.listings.manage', 'Manage all listings')}
                            </Link>
                        </p>
                    )}
                </div>
            ) : user ? (
                isOwnProduct ? (
                    <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 p-5 text-center">
                        <p className="text-base font-semibold text-amber-900 sm:text-lg">
                            {t('product.details.ownproduct.notification')}
                        </p>
                        <p className="mt-2 text-sm text-amber-800/90">
                            {t('product.details.ownproduct')}{' '}
                            <Link to="/my-account" className="font-medium underline">
                                {t('product.details.myaccount')}
                            </Link>
                            .
                        </p>
                    </div>
                ) : holdBlocksBuy ? (
                    <div className="rounded-2xl border border-[var(--accent-color)]/40 bg-[color-mix(in_srgb,var(--bg-color),white_35%)] p-4 text-center shadow-sm">
                        <div className="flex items-center justify-center gap-2 text-base font-semibold text-[var(--text-color)] sm:text-lg">
                            <Clock className="h-5 w-5 text-[var(--accent-color)]" />
                            <span>
                                {tr('checkout.reserved', 'Reserved')}
                                <span className="ml-2 tabular-nums">{countdownLabel}</span>
                            </span>
                        </div>
                        <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                            {tr(
                                'checkout.reserved.hint',
                                'Someone is completing checkout. This item frees if they do not pay in time.'
                            )}{' '}
                            {tr('checkout.reserved.until', 'Released in')} {countdownLabel}.
                        </p>
                        <p className="mt-2 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                            {shippingHint}
                        </p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] p-4 shadow-sm">
                        <button
                            type="button"
                            onClick={handleBuyNow}
                            className="min-h-11 w-full rounded-2xl bg-[var(--accent-color)] px-6 py-3.5 text-base font-semibold text-white shadow-md transition hover:opacity-90 sm:text-lg"
                        >
                            {reservedByMe
                                ? tr('checkout.reserved.continue', 'Continue checkout')
                                : t('product.details.buynow')}
                        </button>
                        <p className="mt-2 text-center text-xs leading-relaxed text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                            {tr('checkout.secure.hint', 'Secure checkout - payment on Shopify')}
                        </p>
                        <p className="mt-1 text-center text-xs leading-relaxed text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                            {shippingHint}
                        </p>
                        {reservedByMe && (
                            <p className="mt-2 text-center text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                                {tr(
                                    'checkout.reserved.continue.hint',
                                    'You started checkout - finish payment or wait for the hold to end.'
                                )}{' '}
                                ({countdownLabel})
                            </p>
                        )}
                    </div>
                )
            ) : (
                <div className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] p-4 text-center shadow-sm">
                    <Link
                        to={`/login?returnUrl=${encodeURIComponent(id ? `/checkout/${id}` : '/home')}`}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-2xl bg-[var(--accent-color)] px-6 py-3 text-base font-semibold text-white shadow-md transition hover:opacity-90"
                    >
                        {t('product.details.loginprompt')}
                    </Link>
                    <p className="mt-2 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {shippingHint}
                    </p>
                </div>
            )}
        </>
    );

    const showMobileStickyBuy = !isSold && !!user && !isOwnProduct && !holdBlocksBuy;

    return (
        <div
            className={`mx-auto max-w-5xl px-1 py-4 sm:px-2 sm:py-6 ${
                showMobileStickyBuy ? 'max-lg:pb-28' : ''
            }`}
        >
            {/* Cream shell - cozy, still content-first */}
            <article className="overflow-hidden rounded-3xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_22%)] shadow-md">
                <div className="grid gap-0 lg:grid-cols-2">
                    {/* Gallery */}
                    <div className="bg-[color-mix(in_srgb,var(--bg-color),white_8%)] p-3 sm:p-4 lg:border-r lg:border-[color-mix(in_srgb,var(--accent-color),transparent_65%)]">
                        <div className="relative aspect-square overflow-hidden rounded-2xl bg-[color-mix(in_srgb,var(--bg-color),transparent_20%)] shadow-inner">
                            {mainImage ? (
                                <img
                                    src={mainImage}
                                    alt={product.title}
                                    className="h-full w-full object-cover"
                                    onError={() => setImageFailed(true)}
                                />
                            ) : (
                                <ProductImageFallback
                                    title={product.title}
                                    label={tr('component.productcard.noimage', 'No image')}
                                    iconClassName="h-14 w-14"
                                />
                            )}
                            {product.condition && (
                                <div className="pointer-events-none absolute left-3 top-3 z-10">
                                    <span className="rounded-full border border-white/40 bg-white/95 px-3 py-1 text-xs font-medium text-[var(--text-color)] shadow-sm backdrop-blur-sm">
                                        {translateProductCondition(tr, product.condition)}
                                    </span>
                                </div>
                            )}
                            {isSold && (
                                <div className="pointer-events-none absolute right-3 top-3 z-10">
                                    <span className="rounded-full bg-[color-mix(in_srgb,var(--text-color),transparent_12%)] px-3 py-1 text-xs font-semibold text-white shadow-sm">
                                        {tr('product.details.sold', 'Sold')}
                                    </span>
                                </div>
                            )}
                            {images.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={goToPrev}
                                        className="absolute left-2 top-1/2 z-10 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/60 sm:left-3"
                                        aria-label="Previous image"
                                    >
                                        <ChevronLeft className="h-6 w-6" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goToNext}
                                        className="absolute right-2 top-1/2 z-10 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/60 sm:right-3"
                                        aria-label="Next image"
                                    >
                                        <ChevronRight className="h-6 w-6" />
                                    </button>
                                    <p className="pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/45 px-2.5 py-0.5 text-xs tabular-nums text-white backdrop-blur-sm">
                                        {currentImageIndex + 1}/{images.length}
                                    </p>
                                </>
                            )}
                        </div>

                        {images.length > 1 && (
                            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide">
                                {images.map((img, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => {
                                            setImageFailed(false);
                                            setCurrentImageIndex(index);
                                        }}
                                        className={`h-16 w-16 shrink-0 snap-start overflow-hidden rounded-xl border-2 transition ${
                                            index === currentImageIndex
                                                ? 'border-[var(--accent-color)] ring-2 ring-[var(--accent-color)]/25'
                                                : 'border-transparent opacity-85 hover:opacity-100'
                                        }`}
                                    >
                                        <img
                                            src={img}
                                            alt=""
                                            className="h-full w-full object-cover"
                                            onError={(e) => {
                                                e.currentTarget.style.visibility = 'hidden';
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Title / buy / seller - primary scan path */}
                    <div className="flex flex-col p-4 sm:p-5 lg:p-6">
                        <header className="mb-4">
                            <h1 className="break-words text-2xl font-bold leading-snug text-[var(--text-color)] sm:text-3xl">
                                {product.title}
                            </h1>
                            <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
                                <p className="text-2xl font-semibold tabular-nums text-[var(--text-color)] sm:text-3xl">
                                    {'\u20AC'}
                                    {formatMoney(product.price)}
                                </p>
                                <p className="inline-flex items-center gap-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                                    <Eye className="h-3.5 w-3.5" aria-hidden />
                                    {product.viewCount} {t('product.details.views')}
                                </p>
                            </div>
                            {/* At-a-glance facts */}
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {product.condition && (
                                    <MetaChip>
                                        {translateProductCondition(tr, product.condition)}
                                    </MetaChip>
                                )}
                                {(product.categories || []).slice(0, 4).map((cat) => (
                                    <MetaChip key={cat}>{translateCategoryName(t, cat)}</MetaChip>
                                ))}
                            </div>
                        </header>

                        {/* Buy stays near title on desktop for efficiency */}
                        <div className="mb-4 hidden lg:block">{buyActions}</div>

                        {/* Personal seller - compact */}
                        {product.sellerId && (
                            <div className="mb-4 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_45%)] p-3 sm:p-3.5">
                                <div className="flex gap-3">
                                    <Link
                                        to={`/users/${product.sellerId}`}
                                        className="shrink-0"
                                        aria-label={sellerName}
                                    >
                                        {avatarUrl ? (
                                            <img
                                                src={avatarUrl}
                                                alt=""
                                                className="h-11 w-11 rounded-full object-cover ring-2 ring-[color-mix(in_srgb,var(--accent-color),transparent_50%)]"
                                            />
                                        ) : (
                                            <div
                                                className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent-color)] text-base font-semibold text-white ring-2 ring-[color-mix(in_srgb,var(--accent-color),transparent_50%)]"
                                                aria-hidden
                                            >
                                                {initials}
                                            </div>
                                        )}
                                    </Link>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm leading-snug text-[var(--text-color)]">
                                            <span className="text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                                {tr('product.details.listedBy', 'Listed by')}{' '}
                                            </span>
                                            <Link
                                                to={`/users/${product.sellerId}`}
                                                className="font-semibold text-[var(--accent-color)] hover:underline"
                                            >
                                                {sellerName}
                                            </Link>
                                            {sellerHeading.subtitle ? (
                                                <span className="text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                                    {' '}
                                                    · {sellerHeading.subtitle}
                                                </span>
                                            ) : null}
                                            {place ? (
                                                <span className="text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                                    {' '}
                                                    {tr('product.details.fromPlace', 'in {place}').replace(
                                                        '{place}',
                                                        place
                                                    )}
                                                </span>
                                            ) : null}
                                        </p>
                                        {product.allowsLocalPickup && (product.pickupCities?.length ?? 0) > 0 ? (
                                            <p className="mt-2 text-sm text-[var(--text-color)]">
                                                {tr('product.pickup.cities', 'Open to local pickup in {cities}').replace(
                                                    '{cities}',
                                                    (product.pickupCities ?? []).join(', ')
                                                )}
                                            </p>
                                        ) : product.allowsLocalPickup ? (
                                            <p className="mt-2 text-sm text-[var(--text-color)]">
                                                {tr('product.pickup.badge', 'Open to local pickup')}
                                            </p>
                                        ) : null}

                                        {hasReviews ? (
                                            <div className="mt-1">
                                                <SellerRatingStars
                                                    averageRating={product.averageSellerRating}
                                                    ratingCount={product.sellerRatingCount}
                                                    size="sm"
                                                />
                                            </div>
                                        ) : isNewSeller ? (
                                            <p className="mt-1 text-xs leading-relaxed text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                                                {tr(
                                                    'seller.new.personal',
                                                    'Just getting started on Petito - a fellow parent in the community.'
                                                )}
                                            </p>
                                        ) : null}

                                        <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                            {soldCount > 0 && (
                                                <span>
                                                    {tr(
                                                        'seller.sold.personal',
                                                        'Already sold {count} items on Petito'
                                                    ).replace('{count}', String(soldCount))}
                                                </span>
                                            )}
                                            {memberSince && !isNewSeller && (
                                                <span>
                                                    {tr('seller.member.since', 'On Petito since {date}').replace(
                                                        '{date}',
                                                        memberSince
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0">
                                            <Link
                                                to={`/users/${product.sellerId}`}
                                                className="inline-flex min-h-11 items-center text-xs font-semibold text-[var(--accent-color)] underline-offset-2 hover:underline"
                                            >
                                                {tr('profile.view', 'View profile')}
                                            </Link>
                                            {!isOwnProduct && (
                                                <Link
                                                    to={`/users/${product.sellerId}`}
                                                    className="inline-flex min-h-11 items-center text-xs font-medium text-[color-mix(in_srgb,var(--text-color),transparent_25%)] underline-offset-2 hover:underline"
                                                >
                                                    {tr('follow.button.personal', 'Follow {name}').replace(
                                                        '{name}',
                                                        sellerName
                                                    )}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Mobile buy after seller so story → person → action */}
                        <div className="mt-auto lg:hidden">{buyActions}</div>
                    </div>
                </div>

                {/* Story + facts - scannable below fold */}
                <div className="space-y-4 border-t border-[color-mix(in_srgb,var(--accent-color),transparent_65%)] p-4 sm:p-5 lg:p-6">
                    {product.description?.trim() && (
                        <section>
                            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[color-mix(in_srgb,var(--text-color),transparent_25%)]">
                                {tr('product.details.about', 'About this item')}
                            </h2>
                            <div className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_65%)] bg-[color-mix(in_srgb,var(--bg-color),white_50%)] px-4 py-3.5">
                                <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-color)]/90 sm:text-base">
                                    {product.description}
                                </p>
                            </div>
                        </section>
                    )}

                    {hasSpecs && (
                        <section>
                            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[color-mix(in_srgb,var(--text-color),transparent_25%)]">
                                {tr('product.details.specifications', 'Specifications')}
                            </h2>
                            <div className="overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_65%)] bg-[color-mix(in_srgb,var(--bg-color),white_50%)]">
                                {specEntries.map(([cat, specs]) => {
                                    const rows = Object.entries(specs || {});
                                    if (rows.length === 0) return null;
                                    return (
                                        <div
                                            key={cat}
                                            className="border-b border-[color-mix(in_srgb,var(--accent-color),transparent_75%)] last:border-b-0"
                                        >
                                            {specEntries.length > 1 && (
                                                <p className="bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] px-4 py-2 text-xs font-semibold text-[var(--accent-color)]">
                                                    {translateCategoryName(t, cat)}
                                                </p>
                                            )}
                                            <dl className="divide-y divide-[color-mix(in_srgb,var(--accent-color),transparent_80%)]">
                                                {rows.map(([key, val]) => (
                                                    <div
                                                        key={key}
                                                        className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-3 px-4 py-2.5 text-sm sm:grid-cols-[minmax(0,12rem)_1fr]"
                                                    >
                                                        <dt className="text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                                                            {key}
                                                        </dt>
                                                        <dd className="font-medium text-[var(--text-color)]">
                                                            {formatSpecDisplayValue(val, tr)}
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}
                </div>
            </article>

            {/* Mobile sticky buy: above bottom nav only when actionable (not own / sold / reserved-by-other) */}
            {showMobileStickyBuy && (
                <div className="fixed inset-x-0 bottom-[calc(var(--mobile-bottom-nav-offset,4rem))] z-30 border-t border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_55%)]/95 p-3 shadow-[0_-6px_24px_rgba(0,0,0,0.08)] backdrop-blur-md lg:hidden">
                    <div className="mx-auto flex max-w-5xl items-center gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-[var(--text-color)]">
                                {'\u20AC'}
                                {formatMoney(product.price)}
                            </p>
                            <p className="truncate text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {product.title}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleBuyNow}
                            className="min-h-11 shrink-0 rounded-xl bg-[var(--accent-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
                        >
                            {reservedByMe
                                ? tr('checkout.reserved.continue', 'Continue checkout')
                                : t('product.details.buynow')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetail;
